import { getBootstrap, getAfericaoAberta, getEmpresasProximas, getEmpresaPorId } from '../offline/api';
import { getAllEmpresas, saveEmpresas, clearEmpresas } from '../db/indexedDb';
import { getNearestCompany } from './getNearestCompany';

/**
 * Carrega empresas com bicos do servidor e salva no IndexedDB
 * @param {boolean} async - Se true, executa de forma assíncrona (não bloqueia). Se false, bloqueia até carregar.
 */
export async function loadEmpresasFromServer(async = false) {
  const logPrefix = async ? '[Bootstrap Async]' : '[Bootstrap]';
  console.log(`${logPrefix} Iniciando carregamento ${async ? 'assíncrono' : 'síncrono'} de empresas...`);
  
  const loadFunction = async () => {
    try {
      const empresas = await getBootstrap();
      console.log(`${logPrefix} Empresas carregadas do servidor:`, empresas?.length || 0);
      
      if (empresas && empresas.length > 0) {
        await saveEmpresas(empresas);
        console.log(`${logPrefix} Empresas salvas no IndexedDB`);
      }
      
      return empresas;
    } catch (error) {
      console.error(`${logPrefix} Erro ao carregar empresas:`, error);
      if (!async) {
        // Se for síncrono, lançar erro para o bootstrap tratar
        throw error;
      }
      // Se for assíncrono, apenas logar
      return null;
    }
  };

  if (async) {
    // Executar em background sem bloquear
    loadFunction().catch(err => {
      console.warn(`${logPrefix} Erro ao carregar empresas em background:`, err);
    });
    return null; // Retorna imediatamente
  } else {
    // Executar de forma síncrona (bloqueia até carregar)
    return await loadFunction();
  }
}

// Variável global para evitar múltiplas chamadas simultâneas de geolocalização
let geolocationInProgress = false;
let geolocationPromise = null;

/**
 * Obtém geolocalização do dispositivo
 * @param {boolean} hasCachedEmpresas - Se true, usa timeout menor
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
async function getGeolocation(hasCachedEmpresas = false) {
  // Se já há uma chamada em andamento, aguardar ela
  if (geolocationInProgress && geolocationPromise) {
    console.log('[Bootstrap] Aguardando geolocalização em andamento...');
    return geolocationPromise;
  }

  if (!navigator.geolocation) {
    throw new Error('Geolocalização não suportada');
  }

  // Marcar como em progresso
  geolocationInProgress = true;
  
  // Timeout: 20 segundos
  const timeout = 20000;
  
  console.log('[Bootstrap] Solicitando geolocalização do dispositivo... (timeout:', timeout / 1000, 's)');
  
  geolocationPromise = new Promise((resolve, reject) => {
    const startTime = Date.now();
    
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const elapsed = Date.now() - startTime;
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        
        console.log(`[Bootstrap] Geolocalização obtida em ${elapsed}ms:`, { lat, lng });
        
        // Limpar estado
        geolocationInProgress = false;
        geolocationPromise = null;
        
        resolve({ latitude: lat, longitude: lng });
      },
      (error) => {
        const elapsed = Date.now() - startTime;
        let errorMsg = 'Erro desconhecido';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Permissão de localização negada pelo usuário';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Informação de localização indisponível';
            break;
          case error.TIMEOUT:
            errorMsg = `Timeout após ${elapsed}ms`;
            break;
          default:
            errorMsg = error.message || 'Erro desconhecido na geolocalização';
            break;
        }
        
        console.warn(`[Bootstrap] Erro na geolocalização após ${elapsed}ms:`, errorMsg);
        
        // Limpar estado
        geolocationInProgress = false;
        geolocationPromise = null;
        
        reject(new Error(`Erro na geolocalização: ${errorMsg}`));
      },
      {
        enableHighAccuracy: false,
        timeout: timeout,
        maximumAge: 0 // Sempre obter posição atual
      }
    );
  });

  return geolocationPromise;
}

/**
 * Fluxo principal de bootstrap (otimizado)
 * 1. Obter geolocalização (primeiro passo, timeout de 20s)
 * 2. Buscar empresas do IndexedDB (cache local)
 * 3. Se não houver no cache:
 *    - Chama API /empresa/proximas passando localização (raio 300m)
 *    - Busca dados completos da empresa mais próxima
 * 4. Se houver no cache:
 *    - Usa empresas do cache
 *    - Calcula empresa mais próxima localmente (haversine, raio 300m)
 * 5. Carrega empresas do servidor (assíncrono, não bloqueia) e guarda no cache
 * 6. Verifica aferição aberta via API
 * @param {Function} onStatusChange - Callback para notificar mudanças de status (ex: obtendoGeolocalizacao)
 */
export async function bootstrapFlow(onStatusChange = null) {
  console.log('[Bootstrap] Iniciando fluxo de bootstrap...');
  
  // Limpar cache de empresas no início para evitar problemas com dados desatualizados
  // Isso garante que sempre buscamos dados frescos do servidor
  try {
    await clearEmpresas();
    console.log('[Bootstrap] Cache de empresas limpo');
  } catch (error) {
    console.warn('[Bootstrap] Erro ao limpar cache de empresas (continuando):', error);
  }

  // 1. Obter geolocalização primeiro (com timeout de 20s)
  let userLat, userLng;
  
  // Notificar que está obtendo geolocalização
  if (onStatusChange) {
    onStatusChange(true);
  }
  
  try {
    const position = await getGeolocation(false);
    
    userLat = position.latitude;
    userLng = position.longitude;
    console.log('[Bootstrap] Geolocalização obtida:', { lat: userLat, lng: userLng });
  } catch (error) {
    // Erro na geolocalização - sempre lançar erro para o usuário ver
    console.error('[Bootstrap] Erro ao obter geolocalização:', error);
    throw error;
  } finally {
    // Notificar que terminou de obter geolocalização
    if (onStatusChange) {
      onStatusChange(false);
    }
  }

  // 2. Buscar empresas do IndexedDB (cache local)
  console.log('[Bootstrap] Buscando empresas do IndexedDB...');
  let empresas = await getAllEmpresas();
  const hasCachedEmpresas = empresas && empresas.length > 0;
  
  let empresaProxima = null;

  // 3. Se não houver no cache: chamar API /empresa/proximas
  if (!hasCachedEmpresas) {
    console.log('[Bootstrap] Nenhuma empresa no IndexedDB, chamando API /empresa/proximas...');
    
    try {
      const empresasProximas = await Promise.race([
        getEmpresasProximas(userLat, userLng, 300),
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Timeout ao buscar empresas próximas')), 10000);
        })
      ]);

      if (!empresasProximas || empresasProximas.length === 0) {
        console.log('[Bootstrap] Nenhuma empresa encontrada no raio de 300m via API');
        // Iniciar carregamento assíncrono de empresas para cache futuro
        setTimeout(() => {
          loadEmpresasFromServer(true).catch(err => {
            console.warn('[Bootstrap] Erro ao carregar empresas em background:', err);
          });
        }, 100);
        
        return {
          empresa: null,
          afericao: null,
          foraDaArea: true
        };
      }

      // Pegar a primeira empresa (mais próxima)
      const empresaProximaBasica = empresasProximas[0];
      console.log('[Bootstrap] Empresa próxima encontrada via API:', {
        id: empresaProximaBasica.id || empresaProximaBasica.Id,
        nome: empresaProximaBasica.nome || empresaProximaBasica.Nome,
        distancia: empresaProximaBasica.distanciaMetros || empresaProximaBasica.DistanciaMetros
      });

      // Buscar dados completos da empresa (com bicos)
      const empresaId = empresaProximaBasica.id || empresaProximaBasica.Id;
      console.log('[Bootstrap] Buscando dados completos da empresa...');
      
      empresaProxima = await Promise.race([
        getEmpresaPorId(empresaId),
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Timeout ao buscar dados completos da empresa')), 10000);
        })
      ]);

      console.log('[Bootstrap] Dados completos da empresa obtidos:', {
        id: empresaProxima.id || empresaProxima.Id,
        nome: empresaProxima.nome || empresaProxima.Nome,
        bicos: empresaProxima.bicos?.length || empresaProxima.Bicos?.length || 0
      });

      // Iniciar carregamento assíncrono de todas as empresas para cache futuro
      setTimeout(() => {
        loadEmpresasFromServer(true).catch(err => {
          console.warn('[Bootstrap] Erro ao carregar empresas em background:', err);
        });
      }, 100);

    } catch (error) {
      console.error('[Bootstrap] Erro ao buscar empresa próxima via API:', error);
      
      // Se for erro de autenticação (401 ou 403), não tratar como "fora da área"
      // O interceptor do axios já vai redirecionar para login
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('[Bootstrap] Erro de autenticação detectado. O usuário será redirecionado para login.');
        // Propagar o erro para que o interceptor do axios possa tratar
        throw error;
      }
      
      // Em caso de outros erros, tentar carregar todas as empresas do servidor como fallback
      try {
        empresas = await loadEmpresasFromServer(false);
        if (empresas && empresas.length > 0) {
          empresaProxima = getNearestCompany(empresas, userLat, userLng);
        }
      } catch (fallbackError) {
        console.error('[Bootstrap] Erro no fallback:', fallbackError);
        
        // Se o fallback também retornar erro de autenticação, propagar
        if (fallbackError.response?.status === 401 || fallbackError.response?.status === 403) {
          throw fallbackError;
        }
      }
      
      // Só retornar "fora da área" se realmente não houver empresa e não for erro de autenticação
      if (!empresaProxima) {
        return {
          empresa: null,
          afericao: null,
          foraDaArea: true
        };
      }
    }
  } else {
    // 4. Se houver no cache: calcular empresa mais próxima localmente
    console.log('[Bootstrap] Empresas encontradas no IndexedDB:', empresas.length);
    console.log('[Bootstrap] Calculando empresa mais próxima localmente...');
    
    empresaProxima = getNearestCompany(empresas, userLat, userLng);

    // 5. Carregar empresas do servidor (assíncrono, não bloqueia) e guardar no cache
    setTimeout(() => {
      loadEmpresasFromServer(true).catch(err => {
        console.warn('[Bootstrap] Erro ao atualizar empresas em background:', err);
      });
    }, 100); // Pequeno delay para não competir com o fluxo principal
  }

  if (!empresaProxima) {
    console.log('[Bootstrap] Nenhuma empresa encontrada no raio de 300m');
    return {
      empresa: null,
      afericao: null,
      foraDaArea: true
    };
  }

  console.log('[Bootstrap] Empresa próxima encontrada:', {
    id: empresaProxima.id || empresaProxima.Id,
    nome: empresaProxima.nome || empresaProxima.Nome
  });

  // 6. Verificar aferição aberta via API
  console.log('[Bootstrap] Verificando aferição aberta via API...');
  let afericao = null;
  const empresaId = empresaProxima.id || empresaProxima.Id;
  
  try {
    afericao = await Promise.race([
      getAfericaoAberta(empresaId),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout ao buscar aferição aberta')), 10000);
      })
    ]);
    console.log('[Bootstrap] Aferição aberta:', afericao ? 'sim' : 'não');
  } catch (error) {
    // Se for erro de autenticação (401 ou 403), propagar o erro
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.warn('[Bootstrap] Erro de autenticação ao buscar aferição aberta. O usuário será redirecionado para login.');
      throw error;
    }
    console.warn('[Bootstrap] Erro ao buscar aferição aberta:', error.message);
    // Continuar mesmo se falhar, assumindo que não há aferição aberta
    afericao = null;
  }

  console.log('[Bootstrap] Fluxo concluído com sucesso');
  return {
    empresa: empresaProxima,
    afericao,
    foraDaArea: false
  };
}
